import { useEffect, useState } from 'react';

import type { AddressSearchResult } from 'src/components/molecules/AddressSearch/services/loqate';
import { search } from 'src/components/molecules/AddressSearch/services/loqate';
import { useDebounce } from './useDebounce';

interface Cancellation {
  isCancelled: boolean;
}

const fetchAddresses = async (
  debouncedQuery: string,
  containerId: string,
  country: string,
  error: string | null,
  cancellation: Cancellation,
  setResults: (result: AddressSearchResult[] | null) => void,
  setError: (error: string | null) => void,
) => {
  try {
    const searchResults = await search(debouncedQuery, containerId, country);
    if (!cancellation.isCancelled) {
      setResults(searchResults);
      setError(null);
    }
  } catch (ex) {
    if (!error) {
      setError((ex as Error).message);
    }
  }
};

export interface UseLoqateSearchData {
  containerId: string;
  error: string | null;
  query: string;
  results: AddressSearchResult[] | null;
  setContainerId: (containerId: string) => void;
  setQuery: (query: string) => void;
}

export const useLoqateSearch = (country: string, debounce: number): UseLoqateSearchData => {
  const [query, setQuery] = useState('');
  const [containerId, setContainerId] = useState('');
  const [results, setResults] = useState<AddressSearchResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const debouncedQuery = useDebounce(query, debounce);

  useEffect(() => {
    if (!debouncedQuery.trim() && !containerId) {
      setResults(null);
      setError(null);
      return;
    }

    const cancellation: Cancellation = {
      isCancelled: false,
    };

    fetchAddresses(debouncedQuery, containerId, country, error, cancellation, setResults, setError);

    return () => {
      cancellation.isCancelled = true;
    };
  }, [debouncedQuery, containerId, country, error]);

  return {
    query,
    containerId,
    error,
    results,
    setQuery,
    setContainerId,
  };
};
